import type { ComponentProps, ReactNode } from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { VariantProps } from 'tailwind-variants';

import { tooltipStyles } from './styles/tooltip.style';

type TooltipStyles = VariantProps<typeof tooltipStyles>;

function TooltipProvider(
  props: ComponentProps<typeof TooltipPrimitive.Provider>,
) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" {...props} />;
}

function TooltipRoot(props: ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({
  className,
  design,
  ...rest
}: ComponentProps<typeof TooltipPrimitive.Trigger> & TooltipStyles) {
  const { trigger: triggerStyles } = tooltipStyles();
  return (
    <TooltipPrimitive.Trigger
      className={triggerStyles({ design, className })}
      data-slot="tooltip-trigger"
      {...rest}
    />
  );
}

function TooltipContent({
  className,
  design,
  sideOffset = 6,
  size,
  ...rest
}: ComponentProps<typeof TooltipPrimitive.Content> & TooltipStyles) {
  const { content: contentStyles } = tooltipStyles();
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={contentStyles({ design, size, className })}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        {...rest}
      />
    </TooltipPrimitive.Portal>
  );
}

function TooltipArrow({
  className,
  design,
  ...rest
}: ComponentProps<typeof TooltipPrimitive.Arrow> & TooltipStyles) {
  const { arrow: arrowStyles } = tooltipStyles();
  return (
    <TooltipPrimitive.Arrow
      className={arrowStyles({ design, className })}
      data-slot="tooltip-arrow"
      {...rest}
    />
  );
}

function TooltipWrapper({
  children,
  content,
  delayDuration,
  design,
  side = 'top',
  size,
}: {
  children: ReactNode;
  content: ReactNode;
  delayDuration?: number;
  design?: TooltipStyles['design'];
  side?: ComponentProps<typeof TooltipPrimitive.Content>['side'];
  size?: TooltipStyles['size'];
}) {
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>

      <TooltipContent design={design} side={side} size={size}>
        {content}
        <TooltipArrow design={design} />
      </TooltipContent>
    </TooltipRoot>
  );
}

export const Tooltip = {
  Arrow: TooltipArrow,
  Content: TooltipContent,
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Wrapper: TooltipWrapper,
};
